import { create } from 'zustand'

/**
 * Background Tasks Store
 * Manages long-running tasks that continue after the originating UI is dismissed.
 * E.g., AI quotation extraction running while user navigates elsewhere.
 */

const TASK_TIMEOUT_MS = 2 * 60 * 1000 // 2 minutes
const AUTO_DISMISS_COMPLETED_MS = 30_000
const AUTO_DISMISS_FAILED_MS = 15_000

export interface TaskResult {
  navigateTo?: string
  message?: string
}

export interface BackgroundTask {
  id: string
  label: string
  status: 'processing' | 'completed' | 'failed'
  result?: TaskResult
  error?: string
  createdAt: number
}

interface BackgroundTasksState {
  tasks: BackgroundTask[]
  addTask: (
    id: string,
    label: string,
    promise: Promise<unknown>,
    onResolve: (data: unknown) => TaskResult
  ) => void
  dismissTask: (id: string) => void
  clearAll: () => void
}

function timeoutPromise(ms: number): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Request timed out. The quotation may still be processing — check your task queue.')), ms)
  )
}

export const useBackgroundTasksStore = create<BackgroundTasksState>((set, get) => ({
  tasks: [],

  addTask: (id, label, promise, onResolve) => {
    const task: BackgroundTask = {
      id,
      label,
      status: 'processing',
      createdAt: Date.now(),
    }

    set((state) => ({ tasks: [...state.tasks, task] }))

    // Race the actual promise against a timeout
    Promise.race([promise, timeoutPromise(TASK_TIMEOUT_MS)])
      .then((data) => {
        const result = onResolve(data)
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, status: 'completed' as const, result } : t
          ),
        }))
        setTimeout(() => {
          get().dismissTask(id)
        }, AUTO_DISMISS_COMPLETED_MS)
      })
      .catch((err) => {
        const errorMessage =
          err instanceof Error ? err.message : 'Something went wrong'
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, status: 'failed' as const, error: errorMessage } : t
          ),
        }))
        setTimeout(() => {
          get().dismissTask(id)
        }, AUTO_DISMISS_FAILED_MS)
      })
  },

  dismissTask: (id) => {
    set((state) => ({ tasks: state.tasks.filter((t) => t.id !== id) }))
  },

  clearAll: () => {
    set({ tasks: [] })
  },
}))
